import React, { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { toast } from "sonner";

export function AbandonedCartsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/abandoned-cash-requests");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not scan abandoned carts");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sendRecovery = async (cardId, phone) => {
    setSending(cardId);
    try {
      await api.post("/admin/abandoned-cash-requests/send-recovery-sms", { card_id: cardId });
      toast.success(`Recovery SMS sent to ${phone}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send recovery SMS");
    } finally {
      setSending(null);
    }
  };

  if (!data && !busy) return null;
  const carts = data?.abandoned || [];

  const fmtAge = (mins) => {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ${mins % 60}m`;
    return `${Math.floor(h / 24)}d ${h % 24}h`;
  };

  return (
    <div className="mt-5 rounded-xl border border-[#E5E0D8] bg-white p-5" data-testid="abandoned-carts-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="serif text-lg text-[#112318]">Abandoned cash-request carts</p>
          <p className="text-xs text-[#4A5C50] mt-1 leading-relaxed max-w-xl">
            Customers who started a cash-request but never finished paying. Stripe
            session is still <span className="mono">open</span> or just <span className="mono">expired</span> —
            we can text them a fresh checkout link to retry. Recovers ~10–20% of starts.
          </p>
        </div>
        <button type="button" onClick={load} disabled={busy}
          className="px-3 py-1.5 rounded-lg border border-[#184A30]/30 text-[#184A30] text-xs hover:bg-[#184A30]/5 disabled:opacity-50"
          data-testid="abandoned-carts-refresh">
          {busy ? "Scanning…" : "Refresh"}
        </button>
      </div>
      {data && (
        <p className="text-[11px] mono text-[#4A5C50] mt-2" data-testid="abandoned-carts-summary">
          {data.scanned} pending cards scanned · {carts.length} abandoned · {data.skipped_already_paid || 0} already paid
        </p>
      )}
      {carts.length > 0 && (
        <ul className="mt-4 divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-xl overflow-hidden">
          {carts.map((c) => (
            <li key={c.card_id} className="flex items-center gap-3 p-3" data-testid={`abandoned-cart-${c.card_id.slice(0,8)}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${
                c.stripe_status === "open" ? "bg-[#184A30]" : "bg-[#CBA35C]"
              }`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#112318] flex items-center gap-2">
                  <span className="mono">{c.card_id.slice(0, 8)}</span>
                  <span className={`text-[10px] mono px-1.5 py-0.5 rounded ${
                    c.stripe_status === "open"
                      ? "bg-[#184A30]/10 text-[#184A30]"
                      : "bg-[#CBA35C]/15 text-[#8C6B21]"
                  }`}>
                    {c.stripe_status}
                  </span>
                  <span className="text-[#4A5C50]">· {c.phone || "(no phone)"}</span>
                </p>
                <p className="text-[11px] mono text-[#4A5C50] mt-0.5">
                  ${Number(c.amount_usd).toFixed(2)} {c.currency} · abandoned {fmtAge(c.age_minutes)} ago
                </p>
              </div>
              <button type="button" onClick={() => sendRecovery(c.card_id, c.phone)}
                disabled={sending === c.card_id || !c.phone}
                className="text-xs px-3 py-1.5 rounded-lg bg-[#D96C4A] hover:bg-[#B04E2F] text-white disabled:opacity-50"
                data-testid={`abandoned-cart-send-${c.card_id.slice(0, 8)}`}>
                {sending === c.card_id ? "Sending…" : "Send recovery SMS"}
              </button>
            </li>
          ))}
        </ul>
      )}
      {data && carts.length === 0 && (
        <p className="text-xs text-[#184A30] mt-3">
          ✓ No abandoned carts in the last 7 days. Great conversion rate!
        </p>
      )}
    </div>
  );
}
